/*
 * Preserve incoming attribution context across MyLabelWater conversion handoffs.
 *
 * Shopify, static Liquid links, and cart checkout forms do not automatically
 * carry landing query params into later product, quote, cart, checkout, or
 * label-designer URLs. Store external campaign/click identifiers for the tab
 * session and append them only to high-intent same-origin destinations when
 * the destination has not already set that parameter.
 */
(function () {
  'use strict';

  var storageKey = 'mylabelwater_attribution_context_v1';
  var clickIdKeys = [
    'gclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'fbclid',
    'ttclid',
    'li_fat_id'
  ];
  var highIntentPathPrefixes = [
    '/products/',
    '/pages/quote',
    '/cart',
    '/checkout',
    '/tools/custom-water-label-maker/'
  ];
  var memoryAttribution = {};

  function cleanValue(value) {
    return (value || '').toString().slice(0, 500);
  }

  function isAttributionKey(key) {
    var normalizedKey = (key || '').toString().toLowerCase();
    return normalizedKey.indexOf('utm_') === 0 || clickIdKeys.indexOf(normalizedKey) !== -1;
  }

  function readStoredAttribution() {
    try {
      if (!window.sessionStorage) return memoryAttribution;

      var raw = window.sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return memoryAttribution;
    }
  }

  function writeStoredAttribution(attribution) {
    memoryAttribution = attribution;
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(storageKey, JSON.stringify(attribution));
      }
    } catch (e) {
      /* First-party storage may be unavailable. Keep the in-memory fallback. */
    }
  }

  function getAttribution() {
    var attribution = readStoredAttribution();
    var changed = false;

    try {
      var params = new URLSearchParams(window.location.search || '');
      params.forEach(function (value, key) {
        if (!isAttributionKey(key)) return;

        var normalizedKey = key.toLowerCase();
        var cleanedValue = cleanValue(value);
        if (!cleanedValue) return;

        attribution[normalizedKey] = cleanedValue;
        changed = true;
      });
    } catch (e) {
      /* URLSearchParams may be unavailable or the current search malformed. */
    }

    if (changed) writeStoredAttribution(attribution);
    return attribution;
  }

  function isHighIntentPath(pathname) {
    return highIntentPathPrefixes.some(function (prefix) {
      return pathname === prefix || pathname.indexOf(prefix) === 0;
    });
  }

  function buildSameOriginUrl(url) {
    return url.pathname + url.search + url.hash;
  }

  function isAnchorElement(target) {
    return typeof HTMLAnchorElement !== 'undefined' && target instanceof HTMLAnchorElement;
  }

  function isFormElement(target) {
    return typeof HTMLFormElement !== 'undefined' && target instanceof HTMLFormElement;
  }

  function getUrlAttributeName(target) {
    if (isAnchorElement(target)) return 'href';
    if (isFormElement(target)) return 'action';
    return '';
  }

  function decorateTarget(target, attribution) {
    if (!target || target.dataset.preserveAttribution === 'false') return;
    if (!attribution || !Object.keys(attribution).length) return;

    var attributeName = getUrlAttributeName(target);
    if (!attributeName) return;

    var rawUrl = target.getAttribute(attributeName);
    if (!rawUrl || rawUrl.charAt(0) === '#') return;

    var url;
    try {
      url = new URL(rawUrl, window.location.href);
    } catch (e) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!isHighIntentPath(url.pathname)) return;

    var changed = false;
    Object.keys(attribution).forEach(function (key) {
      if (url.searchParams.has(key)) return;

      url.searchParams.set(key, attribution[key]);
      changed = true;
    });

    if (changed) {
      target.setAttribute(attributeName, buildSameOriginUrl(url));
    }
  }

  function decorateTargets(root) {
    var attribution = getAttribution();
    if (!Object.keys(attribution).length) return;

    if (isAnchorElement(root) || isFormElement(root)) {
      decorateTarget(root, attribution);
    }

    if (!root.querySelectorAll) return;

    root.querySelectorAll('a[href], form[action]').forEach(function (target) {
      decorateTarget(target, attribution);
    });
  }

  function decorateEventTarget(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var urlTarget = target.closest('a[href], form[action]');
    if (urlTarget) decorateTarget(urlTarget, getAttribution());
  }

  getAttribution();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      decorateTargets(document);
    }, { once: true });
  } else {
    decorateTargets(document);
  }

  ['pointerdown', 'touchstart', 'focusin', 'click', 'submit'].forEach(function (eventName) {
    document.addEventListener(eventName, decorateEventTarget, true);
  });

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) decorateTargets(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
